import React, { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle, XCircle, AlertTriangle, Clock, Shield, Activity, Zap } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

function TrafficLight({ color }) {
  const colors = {
    green: "bg-emerald-500",
    amber: "bg-amber-500",
    red: "bg-red-500",
    gray: "bg-slate-300"
  };
  
  return (
    <div className="flex gap-2">
      <div className={`w-6 h-6 rounded-full ${color === "red" ? colors.red : colors.gray}`} />
      <div className={`w-6 h-6 rounded-full ${color === "amber" ? colors.amber : colors.gray}`} />
      <div className={`w-6 h-6 rounded-full ${color === "green" ? colors.green : colors.gray}`} />
    </div>
  );
}

function SectionCard({ section }) {
  const isPass = section.passed;
  
  return (
    <div 
      className={`rounded-2xl border p-4 ${isPass ? "border-emerald-200 bg-emerald-50" : "border-red-200 bg-red-50"}`}
      data-testid={`section-${section.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {isPass ? (
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          ) : (
            <XCircle className="w-5 h-5 text-red-600" />
          )}
          <span className="font-semibold text-slate-800">{section.name}</span>
        </div>
        <span className={`text-sm font-bold ${isPass ? "text-emerald-700" : "text-red-700"}`}>
          {section.score}%
        </span>
      </div>
      
      <div className="mt-2 text-sm text-slate-600">
        {section.passed_checks}/{section.total_checks} checks passed
      </div>
      
      {section.failures?.length > 0 && (
        <div className="mt-3 space-y-1">
          {section.failures.slice(0, 3).map((f, i) => (
            <div key={i} className="text-sm text-red-700 bg-red-100 rounded-lg px-3 py-1">
              {f}
            </div>
          ))}
          {section.failures.length > 3 && (
            <div className="text-xs text-red-600">+{section.failures.length - 3} more issues</div>
          )}
        </div>
      )}
      
      {section.warnings?.length > 0 && (
        <div className="mt-2 space-y-1">
          {section.warnings.slice(0, 2).map((w, i) => (
            <div key={i} className="text-sm text-amber-700 bg-amber-100 rounded-lg px-3 py-1 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {w}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SLACard({ sla }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        <Clock className="w-5 h-5 text-teal-600" />
        <h3 className="text-lg font-semibold text-slate-800">SLA Status</h3>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="rounded-xl bg-red-50 p-3 text-center">
          <div className="text-2xl font-bold text-red-700">{sla?.breached || 0}</div>
          <div className="text-xs text-red-600">Breached</div>
        </div>
        <div className="rounded-xl bg-amber-50 p-3 text-center">
          <div className="text-2xl font-bold text-amber-700">{sla?.at_risk || 0}</div>
          <div className="text-xs text-amber-600">At Risk</div>
        </div>
        <div className="rounded-xl bg-emerald-50 p-3 text-center">
          <div className="text-2xl font-bold text-emerald-700">{sla?.on_track || 0}</div>
          <div className="text-xs text-emerald-600">On Track</div>
        </div>
        <div className="rounded-xl bg-purple-50 p-3 text-center">
          <div className="text-2xl font-bold text-purple-700">{sla?.escalated || 0}</div>
          <div className="text-xs text-purple-600">Escalated</div>
        </div>
      </div>
      
      <div className="mt-4 text-xs text-slate-500">
        {sla?.chases_24h || 0} chase messages sent in last 24h
      </div>
    </div>
  );
}

export default function LockdownDashboardPage() {
  const [audit, setAudit] = useState(null);
  const [sla, setSla] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runningFixes, setRunningFixes] = useState(false);
  const [runningSla, setRunningSla] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  
  const loadAudit = async () => {
    try {
      setLoading(true);
      const [auditRes, slaRes] = await Promise.all([
        axios.get(`${API}/api/lockdown/audit`),
        axios.get(`${API}/api/lockdown/sla/dashboard`)
      ]);
      setAudit(auditRes.data);
      setSla(slaRes.data);
      setLastRun(auditRes.data.audited_at);
    } catch (err) {
      console.error("Failed to load audit:", err);
    } finally {
      setLoading(false);
    }
  };
  
  const runFullLockdown = async () => {
    try {
      setRunning(true);
      const res = await axios.post(`${API}/api/lockdown/run`);
      setAudit(res.data.audit);
      setSla(res.data.sla);
      setLastRun(new Date().toISOString());
    } catch (err) {
      console.error("Failed to run lockdown:", err);
      alert("Failed to run lockdown check");
    } finally {
      setRunning(false);
    }
  };
  
  const runSafeAutoFixes = async () => {
    try {
      setRunningFixes(true);
      const res = await axios.post(`${API}/api/lockdown/audit/run-fixes`);
      alert(`Fixes applied: ${res.data.fixes_applied?.length || 0}\nErrors: ${res.data.errors?.length || 0}`);
      await loadAudit();
    } catch (err) {
      console.error("Failed to run fixes:", err);
      alert("Failed to run auto-fixes");
    } finally {
      setRunningFixes(false);
    }
  };
  
  const runSlaEngine = async () => {
    try {
      setRunningSla(true);
      const res = await axios.post(`${API}/api/lockdown/sla/run`);
      alert(`SLA Engine Run Complete:\n- Breached: ${res.data.breached}\n- Chased: ${res.data.chased}\n- Escalated: ${res.data.escalated}`);
      await loadAudit();
    } catch (err) {
      console.error("Failed to run SLA engine:", err);
      alert("Failed to run SLA engine");
    } finally {
      setRunningSla(false);
    }
  };
  
  useEffect(() => {
    loadAudit();
  }, []);
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-teal-600 border-t-transparent" />
      </div>
    );
  }
  
  const goLiveReady = audit?.go_live_ready;
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="lockdown-dashboard">
      {/* Header */}
      <div className={`${goLiveReady ? "bg-gradient-to-r from-emerald-700 to-emerald-600" : "bg-gradient-to-r from-red-700 to-red-600"} text-white`}>
        <div className="mx-auto max-w-7xl px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Shield className="w-10 h-10" />
              <div>
                <h1 className="text-3xl font-semibold">System Lockdown Audit</h1>
                <p className="mt-1 text-sm opacity-90">Pre-Live GO/NO-GO Verification</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <TrafficLight color={audit?.traffic || "gray"} />
              <div className={`rounded-2xl px-6 py-3 ${goLiveReady ? "bg-white/20" : "bg-white/10"}`}>
                <div className="text-3xl font-bold">{audit?.overall_score || 0}%</div>
                <div className="text-sm font-medium">
                  {goLiveReady ? "GO LIVE READY" : "NOT READY"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Action Bar */}
      <div className="mx-auto max-w-7xl px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="text-sm text-slate-500">
            Last run: {lastRun ? new Date(lastRun).toLocaleString() : "Never"}
          </div>
          <div className="flex gap-2">
            <button
              onClick={loadAudit}
              disabled={loading}
              className="rounded-full bg-slate-100 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-200 transition flex items-center gap-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={runSlaEngine}
              disabled={runningSla}
              className="rounded-full bg-purple-100 px-4 py-2 text-sm font-medium text-purple-700 hover:bg-purple-200 transition flex items-center gap-2"
              data-testid="run-sla-btn"
            >
              <Activity className={`w-4 h-4 ${runningSla ? "animate-pulse" : ""}`} />
              {runningSla ? "Running..." : "Run SLA Engine"}
            </button>
            <button
              onClick={runSafeAutoFixes}
              disabled={runningFixes}
              className="rounded-full bg-amber-100 px-4 py-2 text-sm font-medium text-amber-700 hover:bg-amber-200 transition flex items-center gap-2"
              data-testid="run-fixes-btn"
            >
              <Zap className={`w-4 h-4 ${runningFixes ? "animate-pulse" : ""}`} />
              {runningFixes ? "Fixing..." : "Auto-Fix Safe Issues"}
            </button>
            <button
              onClick={runFullLockdown}
              disabled={running}
              className="rounded-full bg-teal-600 px-6 py-2 text-sm font-semibold text-white hover:bg-teal-700 transition flex items-center gap-2"
              data-testid="run-lockdown-btn"
            >
              <Shield className={`w-4 h-4 ${running ? "animate-pulse" : ""}`} />
              {running ? "Running..." : "Run Full Lockdown"}
            </button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="mx-auto max-w-7xl px-6 pb-8">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 text-center">
            <div className="text-3xl font-bold text-slate-800">{audit?.total_checks || 0}</div>
            <div className="text-sm text-slate-500">Total Checks</div>
          </div>
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-center">
            <div className="text-3xl font-bold text-emerald-700">{audit?.passed_checks || 0}</div>
            <div className="text-sm text-emerald-600">Passed</div>
          </div>
          <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-center">
            <div className="text-3xl font-bold text-red-700">{audit?.critical_failures || 0}</div>
            <div className="text-sm text-red-600">Critical Failures</div>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-center">
            <div className="text-3xl font-bold text-amber-700">{audit?.warnings || 0}</div>
            <div className="text-sm text-amber-600">Warnings</div>
          </div>
        </div>
        
        {/* SLA Dashboard */}
        <div className="mb-6">
          <SLACard sla={sla} />
        </div>
        
        {/* Critical Failures */}
        {audit?.all_failures?.length > 0 && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-5">
            <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
              <XCircle className="w-5 h-5" />
              Critical Failures ({audit.all_failures.length})
            </h3>
            <div className="mt-3 space-y-2">
              {audit.all_failures.map((f, i) => (
                <div key={i} className="text-sm text-red-700 bg-white rounded-lg px-4 py-2 border border-red-200">
                  {f}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Warnings */}
        {audit?.all_warnings?.length > 0 && (
          <div className="mb-6 rounded-2xl border border-amber-200 bg-amber-50 p-5">
            <h3 className="text-lg font-semibold text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Warnings ({audit.all_warnings.length})
            </h3>
            <div className="mt-3 space-y-2">
              {audit.all_warnings.slice(0, 10).map((w, i) => (
                <div key={i} className="text-sm text-amber-700 bg-white rounded-lg px-4 py-2 border border-amber-200">
                  {w}
                </div>
              ))}
              {audit.all_warnings.length > 10 && (
                <div className="text-xs text-amber-600 mt-2">
                  +{audit.all_warnings.length - 10} more warnings
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* Section Details */}
        <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="text-xl font-semibold text-slate-800 mb-4">Audit Sections</h3>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {audit?.sections?.map((section, i) => (
              <SectionCard key={i} section={section} />
            ))}
          </div>
        </div>
        
        {/* Go Live Decision */}
        <div className={`mt-6 rounded-3xl p-6 text-center ${goLiveReady ? "bg-emerald-100 border-2 border-emerald-300" : "bg-red-100 border-2 border-red-300"}`}>
          <div className="flex items-center justify-center gap-3">
            {goLiveReady ? (
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            ) : (
              <XCircle className="w-10 h-10 text-red-600" />
            )}
            <div>
              <div className={`text-2xl font-bold ${goLiveReady ? "text-emerald-800" : "text-red-800"}`}>
                {goLiveReady ? "SYSTEM READY FOR GO-LIVE" : "SYSTEM NOT READY FOR GO-LIVE"}
              </div>
              <div className={`text-sm ${goLiveReady ? "text-emerald-600" : "text-red-600"}`}>
                {goLiveReady 
                  ? "All critical checks passed. Safe to proceed with production deployment."
                  : `${audit?.critical_failures || 0} critical issues must be resolved before go-live.`
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
